import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import {
    addNavigatorFolderResources,
    addNavigatorTagResources,
    changeNavigatorFolderOrder,
    changeNavigatorTagOrder,
    deleteNavigatorBulkFolder,
    deleteNavigatorBulkTag,
    getNavigatorFolders,
    getNavigatorResources,
    getNavigatorTags,
    mergeNavigatorFolder,
    postNavigatorFolder,
    postNavigatorTag,
    putNavigatorFolder,
    putNavigatorTag,
} from "utils/request/navigator";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

const invalidateNavigatorQueries = (queryClient) => {
    queryClient.invalidateQueries({ queryKey: "navigatorResources" });
    queryClient.invalidateQueries({ queryKey: "navigatorFolders" });
    queryClient.invalidateQueries({ queryKey: "navigatorTags" });
    queryClient.invalidateQueries({ queryKey: "reference" });
    queryClient.invalidateQueries({ queryKey: "referenceList" });
    queryClient.invalidateQueries({ queryKey: "notes" });
    queryClient.invalidateQueries({ queryKey: "manuscripts" });
    queryClient.invalidateQueries({ queryKey: "research" });
};

export const useNavigatorResources = (params, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const { enabled: paramsEnabled, ...requestParams } = params || {};
    const enabled = options.enabled ?? paramsEnabled ?? true;

    return useQuery(
        ["navigatorResources", space_uuid, requestParams],
        () => getNavigatorResources(space_uuid, requestParams),
        {
            staleTime: Infinity,
            cacheTime: Infinity,
            retry: false,
            keepPreviousData: true,
            ...options,
            enabled,
        },
    );
};

export const useNavigatorFolders = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["navigatorFolders", space_uuid], () => getNavigatorFolders(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useNavigatorTags = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["navigatorTags", space_uuid], () => getNavigatorTags(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useNavigatorFolderResourcesCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => addNavigatorFolderResources(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorTagResourcesCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => addNavigatorTagResources(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorTagCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postNavigatorTag(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorTagUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => putNavigatorTag(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorTagBulkDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => deleteNavigatorBulkTag(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorTagChangeOrder = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => changeNavigatorTagOrder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorFolderCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postNavigatorFolder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorFolderUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => putNavigatorFolder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorFolderDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => deleteNavigatorBulkFolder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorFolderChangeOrder = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => changeNavigatorFolderOrder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};

export const useNavigatorFolderMerge = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => mergeNavigatorFolder(space_uuid, body), {
        onSuccess: () => {
            invalidateNavigatorQueries(queryClient);
        },
    });
};
